import { useState, useEffect } from "react";
import { X, Sparkles, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { getCreditAddingPlan, createCreditOrder, verifyCreditPayment, getUserCredits } from "../apis/subscription";
import { loadScript } from "../utils/razorpay";
import { logEvent } from "../apis/events";
import { formatPayable } from "../utils/paymentCharges";
import { useBranding } from "../contexts/BrandingContext";
import { resolveCheckoutBrand } from "../utils/checkoutBrand";
import PaymentBreakdown from "./PaymentBreakdown";

const QUICK_CREDITS = [10, 25, 50, 100];

const CreditPurchaseModal = ({ isOpen, onClose, onSuccess }) => {
  const brand = useBranding();
  const checkout = resolveCheckoutBrand(brand);
  const [addingPlan, setAddingPlan] = useState(null);
  const [creditsAmount, setCreditsAmount] = useState("10");
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [userCredits, setUserCredits] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setCreditsAmount("10");
      loadAddingPlan();
      loadUserCredits();
    }
  }, [isOpen]);

  const loadAddingPlan = async () => {
    try {
      setIsLoading(true);
      const response = await getCreditAddingPlan();
      if (response.data.success) {
        setAddingPlan(response.data.data);
      } else {
        toast.error(response.data.error || "Failed to load credit plan");
      }
    } catch (error) {
      console.error("Error loading adding plan:", error);
      toast.error("Failed to load credit plan");
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserCredits = async () => {
    try {
      const response = await getUserCredits();
      if (response.data.success) {
        setUserCredits(response.data.data);
      }
    } catch (error) {
      console.error("Error loading user credits:", error);
    }
  };

  const handlePurchase = async () => {
    const creditsNum = parseInt(creditsAmount) || 0;
    if (!addingPlan || creditsNum <= 0) {
      toast.error("Please enter credits amount");
      return;
    }

    setIsProcessing(true);

    try {
      await loadScript();

      const orderResponse = await createCreditOrder(addingPlan.id, creditsNum);
      if (!orderResponse.data.success) {
        throw new Error(orderResponse.data.error || "Failed to create order");
      }

      const { order_id, amount, key_id } = orderResponse.data.data;

      const options = {
        key: key_id,
        amount: amount,
        currency: "INR",
        name: checkout.name,
        image: checkout.image || undefined,
        description: `Purchase ${creditsNum} AI Credits`,
        order_id: order_id,
        handler: async function (response) {
          try {
            const verifyResponse = await verifyCreditPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            if (verifyResponse.data.success) {
              toast.success(`Successfully purchased ${creditsNum} credits!`);
              await loadUserCredits();
              if (onSuccess) {
                onSuccess(verifyResponse.data.data);
              }
              onClose();
            } else {
              throw new Error(verifyResponse.data.error || "Payment verification failed");
            }
          } catch (error) {
            console.error("Payment verification error:", error);
            toast.error(error.response?.data?.error || "Payment verification failed");
          } finally {
            setIsProcessing(false);
          }
        },
        prefill: {
          name: JSON.parse(localStorage.getItem("user") || "{}").name || "",
          email: JSON.parse(localStorage.getItem("user") || "{}").email || "",
        },
        theme: {
          color: "#16A34A",
        },
        modal: {
          ondismiss: function () {
            logEvent("payment_abandoned", {
              kind: "ai_credits",
              credits_amount: creditsNum,
              order_id,
            });
            setIsProcessing(false);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error("Purchase error:", error);
      toast.error(error.response?.data?.error || "Failed to initiate payment");
      setIsProcessing(false);
    }
  };

  const getCreditsNumber = () => parseInt(creditsAmount) || 0;

  const calculateTotal = () => {
    const creditsNum = getCreditsNumber();
    if (!addingPlan || creditsNum <= 0) return 0;
    return (addingPlan.per_credit_price * creditsNum).toFixed(2);
  };

  const calculateReplies = () => {
    const creditsNum = getCreditsNumber();
    if (!addingPlan || creditsNum <= 0) return 0;
    return creditsNum * addingPlan.per_credit_ai_replies;
  };

  const handleCreditsChange = (e) => {
    const value = e.target.value;
    if (value === "") {
      setCreditsAmount("");
      return;
    }
    if (/^\d+$/.test(value)) {
      setCreditsAmount(value);
    }
  };

  if (!isOpen) return null;

  const creditsNum = getCreditsNumber();
  const replies = userCredits?.totalAvailableReplies ?? 0;

  return (
    <>
      <div
        className="ping-drawer-backdrop"
        onClick={() => !isProcessing && onClose()}
      />
      <aside className="ping-drawer" role="dialog" aria-modal="true" aria-label="Purchase AI credits">
        <div className="ping-drawer-head">
          <div>
            <h3>Purchase AI credits</h3>
            <p>
              {userCredits
                ? `${Number(replies).toLocaleString("en-IN")} replies available now`
                : "Pay with Razorpay · UPI, cards, netbanking"}
            </p>
          </div>
          <button
            type="button"
            className="camp-ico"
            onClick={() => !isProcessing && onClose()}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="ping-drawer-body">
          {isLoading ? (
            <div className="cred-loading">
              <Loader2 size={18} className="animate-spin" />
              Loading plan…
            </div>
          ) : !addingPlan ? (
            <div className="ping-banner">No credit plan available. Please contact support.</div>
          ) : (
            <>
              <div className="cred-plan">
                <div>
                  <span>Plan</span>
                  <b>{addingPlan.plan_name}</b>
                </div>
                <div>
                  <span>Per credit</span>
                  <b>₹{Number(addingPlan.per_credit_price).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</b>
                </div>
                <p>1 credit = {addingPlan.per_credit_ai_replies} AI replies</p>
              </div>

              <label style={{ marginBottom: 10 }}>Choose credits</label>
              <div className="wallet-amounts">
                {QUICK_CREDITS.map((v) => (
                  <button
                    key={v}
                    type="button"
                    className={String(v) === creditsAmount ? "on" : ""}
                    onClick={() => setCreditsAmount(String(v))}
                  >
                    {v}
                    <small>{v * addingPlan.per_credit_ai_replies} replies</small>
                  </button>
                ))}
              </div>

              <div className="wallet-amount-box cred-amount-box">
                <label htmlFor="credit-amount">Custom amount</label>
                <div className="field">
                  <Sparkles size={16} />
                  <input
                    id="credit-amount"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={creditsAmount}
                    onChange={handleCreditsChange}
                    placeholder="Number of credits"
                  />
                </div>
              </div>

              {creditsNum > 0 && (
                <>
                  <div className="cred-total">
                    <div>
                      <span>You get</span>
                      <b>{calculateReplies().toLocaleString("en-IN")} AI replies</b>
                    </div>
                    <div>
                      <span>Credits</span>
                      <b>₹{Number(calculateTotal()).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</b>
                    </div>
                  </div>
                  <PaymentBreakdown base={calculateTotal()} creditLabel="Credits amount" />
                </>
              )}
            </>
          )}
        </div>

        <div className="ping-drawer-foot">
          <button
            type="button"
            className="ping-btn ping-btn-ghost ping-btn-sm"
            onClick={() => !isProcessing && onClose()}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ping-btn ping-btn-primary ping-btn-sm disabled:opacity-50"
            onClick={handlePurchase}
            disabled={!addingPlan || creditsNum <= 0 || isProcessing || isLoading}
          >
            {isProcessing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {isProcessing
              ? "Processing…"
              : `Pay${creditsNum > 0 ? ` ₹${formatPayable(calculateTotal())}` : ""}`}
          </button>
        </div>
      </aside>
    </>
  );
};

export default CreditPurchaseModal;
